import { useId, useMemo, useState } from "react";

import { Check, ChevronDown, Search } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";
import { ScrollArea } from "@components/ui/scroll-area";

import { cn } from "@utils/ui";

export type SearchableSelectOption = {
  value: string;
  label: string;
  /** Secondary line under the label — e.g. an email. Also searched. */
  hint?: string;
  /** Rendered but not choosable. For representing existing state that isn't a
   *  valid choice (e.g. a case owner who isn't a doctor). */
  disabled?: boolean;
  /** Muted note appended to the row, for disabled entries. */
  note?: string;
};

type SearchableSelectProps = {
  options: SearchableSelectOption[];
  value?: string | null;
  onChange: (next: string) => void;
  /** Trigger text when nothing is selected. Also the "clear" row's label. */
  placeholder?: string;
  searchPlaceholder?: string;
  ariaLabel: string;
  disabled?: boolean;
  /** Allow clearing back to no selection (renders the placeholder as a row). */
  clearable?: boolean;
  className?: string;
};

/**
 * Searchable single-select on the house Popover primitives.
 *
 * Replaces a native `<select>` where the option list can get long enough to need
 * filtering — the assigned-doctor and referrer pickers grow with the user table.
 * A native select also renders with OS chrome that ignores the design system, and
 * can't show a two-line option (name + email) or a muted note.
 *
 * Mirrors `MultiSelect`: same Popover + search + ScrollArea structure and the same
 * trigger styling, so the two read as one family.
 */
export const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  ariaLabel,
  disabled = false,
  clearable = true,
  className,
}: SearchableSelectProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Index into the choosable rows for arrow-key navigation. -1 = nothing active.
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchId = useId();
  const listboxId = useId();

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.hint ? o.hint.toLowerCase().includes(q) : false)
    );
  }, [options, query]);

  const choose = (next: string) => {
    onChange(next);
    setOpen(false);
    setQuery("");
    setActiveIndex(-1);
  };

  // Only rows that can actually be picked take part in navigation, so arrowing
  // never lands on the disabled "not a doctor" row.
  const navigable = filtered.filter((o) => !o.disabled);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!navigable.length) return;
      const step = e.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((prev) => {
        const next = prev + step;
        if (next < 0) return navigable.length - 1;
        if (next >= navigable.length) return 0;
        return next;
      });
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const target = navigable[activeIndex];
      if (target) choose(target.value);
      return;
    }
    if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
      setActiveIndex(-1);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (disabled) return;
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn(
            "flex h-11 w-full items-center justify-between gap-2 rounded-lg border border-foreground-muted/25 bg-background-white px-3 text-left text-[13.5px] shadow-sm outline-none",
            "focus-visible:ring-2 focus-visible:ring-primary/40",
            disabled && "cursor-not-allowed opacity-60",
            className
          )}
        >
          <span className="min-w-0 truncate">
            {selected ? (
              <>
                {selected.label}
                {selected.note && (
                  <span className="text-foreground-muted"> — {selected.note}</span>
                )}
              </>
            ) : (
              <span className="text-foreground-muted">{placeholder}</span>
            )}
          </span>
          <ChevronDown className="size-4 flex-none text-foreground-muted" />
        </button>
      </PopoverTrigger>

      {/* `--radix-popover-content-available-height` caps the popover to the space
          Radix measured, so opening near the bottom of the page scrolls the list
          instead of running off-screen. `flex flex-col` lets the search box stay
          fixed while the list takes the remaining height. */}
      <PopoverContent
        align="start"
        collisionPadding={8}
        className="flex max-h-[--radix-popover-content-available-height] w-[--radix-popover-trigger-width] flex-col p-0"
      >
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="size-3.5 flex-none text-foreground-muted" />
          <input
            id={searchId}
            aria-label={`${ariaLabel} search`}
            className="w-full bg-transparent text-[13px] outline-none placeholder:text-foreground-muted"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(-1);
            }}
            onKeyDown={handleKeyDown}
          />
        </div>

        {/* Definite height, not max-height: Radix's Viewport is `size-full`, so a
            max-height leaves it unconstrained and the list overflows the popover.
            Sized to the row count (~44px each) and capped, so a 2-option referrer
            list doesn't render 270px of blank space. */}
        <ScrollArea
          className="min-h-0"
          style={{
            height: Math.min(
              273,
              Math.max(44, (filtered.length + (clearable ? 1 : 0)) * 44)
            ),
          }}
        >
          <div id={listboxId} role="listbox" aria-label={ariaLabel} className="p-1">
            {clearable && (
              <button
                type="button"
                role="option"
                aria-selected={!value}
                className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-[13px] text-foreground-muted hover:bg-primary-50"
                onClick={() => choose("")}
              >
                <span className="size-3.5 flex-none">
                  {!value && <Check className="size-3.5" strokeWidth={2.5} />}
                </span>
                {placeholder}
              </button>
            )}

            {filtered.map((option) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  disabled={option.disabled}
                  className={cn(
                    "flex w-full items-start gap-2 rounded-md px-2 py-2 text-left text-[13px]",
                    option.disabled
                      ? "cursor-not-allowed text-foreground-muted"
                      : "hover:bg-primary-50",
                    !option.disabled &&
                      navigable[activeIndex]?.value === option.value &&
                      "bg-primary-50"
                  )}
                  onClick={() => !option.disabled && choose(option.value)}
                >
                  <span className="mt-0.5 size-3.5 flex-none">
                    {isSelected && <Check className="size-3.5" strokeWidth={2.5} />}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate font-medium">
                      {option.label}
                      {option.note && (
                        <span className="font-normal text-foreground-muted">
                          {" "}
                          — {option.note}
                        </span>
                      )}
                    </span>
                    {option.hint && (
                      <span className="block truncate text-xs text-foreground-muted">
                        {option.hint}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}

            {filtered.length === 0 && (
              <p className="px-2 py-6 text-center text-[13px] text-foreground-muted">
                No matches
              </p>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
